import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { formatDate, getTimezoneAbbreviation, relative_dates, RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import { storage_keys } from '../../../../utils/storage';
import Button from '../../../common/base/Button';
import { StyledCardSection } from '../../../common/base/Card';
import Text from '../../../common/base/Text';
import Dropdown from '../../../common/Dropdown';
import DatePickerInput, { formatDateFilterValues } from '../../../common/Form/Fields/DatePickerInput';
import { BROWSER_TIMEZONE_OPTION, resolveTimezoneOption } from '../../../common/Form/Fields/TimezoneSelectInput';
import { Div } from '../../../common/helpers/StyledUtils';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { useDatePreferences, useUpdateTimezonePreference } from '../../../hooks/useDatePreferences';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { DashboardContext } from '../DashboardContext';
import { getTimerangeLabel } from '../Events/Histogram';
import { useRefreshKey } from './RefreshKeyContext';
export type DateRange = {
  min?: string;
  max?: string;
  relative?: RelativeDate;
};
export const getDateRangeQueryParam = (date_range?: DateRange) => date_range?.relative ? date_range.relative : date_range;
interface Props {
  date_range: DateRange;
  onDateRangeChanged: (date_range: DateRange) => void;
  portal?: boolean;
}
export const useDateRange = t0 => {
  const $ = _c(13);
  const default_relative = t0 === undefined ? "yesterday" as keyof typeof relative_dates : t0;
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const [stored_relative_date, setStoredRelativeDate] = useLocalStorage(storage_keys.metric_date_range_relative, null);
  const stored_default_relative = stored_relative_date && relative_dates[stored_relative_date] ? stored_relative_date : default_relative;
  let date_range;
  if ($[0] !== default_relative || $[1] !== query.date || $[2] !== refresh_key || $[3] !== stored_default_relative) {
    date_range = typeof query.date === "string" ? {
      relative: query.date as RelativeDate
    } : query.date as DateRange;
    if (!query.date) {
      date_range = {
        relative: stored_default_relative
      };
    }
    if (date_range.relative) {
      const {
        relative,
        min,
        max
      } = resolveRelativeDate(date_range.relative, default_relative, new Date(refresh_key));
      date_range = {
        ...date_range,
        relative,
        min: min.toISOString(),
        max: max.toISOString()
      };
    }
    $[0] = default_relative;
    $[1] = query.date;
    $[2] = refresh_key;
    $[3] = stored_default_relative;
    $[4] = date_range;
  } else {
    date_range = $[4];
  }
  let t1;
  if ($[5] !== default_relative || $[6] !== setStoredRelativeDate || $[7] !== updateRefreshKey || $[8] !== updateSearchQuery) {
    t1 = date_range_0 => {
      updateRefreshKey();
      if (date_range_0.relative) {
        const {
          relative: relative_0
        } = resolveRelativeDate(date_range_0.relative, default_relative);
        setStoredRelativeDate(relative_0);
        updateSearchQuery({
          date: {
            relative: relative_0
          }
        });
        return;
      }
      updateSearchQuery({
        date: date_range_0
      });
    };
    $[5] = default_relative;
    $[6] = setStoredRelativeDate;
    $[7] = updateRefreshKey;
    $[8] = updateSearchQuery;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const onDateRangeChanged = t1;
  let t2;
  if ($[10] !== date_range || $[11] !== onDateRangeChanged) {
    t2 = [date_range, onDateRangeChanged];
    $[10] = date_range;
    $[11] = onDateRangeChanged;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
const MetricDatePicker: React.FC<Props> = t0 => {
  const $ = _c(35);
  const {
    date_range,
    onDateRangeChanged,
    portal: t1
  } = t0;
  const portal = t1 === undefined ? false : t1;
  const {
    browser_timezone,
    timezone,
    uses_browser_timezone
  } = useDatePreferences();
  const updateTimezonePreference = useUpdateTimezonePreference();
  const t2 = date_range.min as string;
  let t3;
  if ($[0] !== t2 || $[1] !== timezone) {
    t3 = formatDate(t2, "HH:mm:ss", timezone);
    $[0] = t2;
    $[1] = timezone;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = date_range.max as string;
  let t5;
  if ($[3] !== t4 || $[4] !== timezone) {
    t5 = formatDate(t4, "HH:mm:ss", timezone);
    $[3] = t4;
    $[4] = timezone;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = uses_browser_timezone ? BROWSER_TIMEZONE_OPTION : timezone;
  let t7;
  if ($[6] !== date_range || $[7] !== t3 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      date_range,
      start_time: t3,
      end_time: t5,
      timezone: t6
    };
    $[6] = date_range;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const form_initial_values = t7;
  const {
    subscription,
    team
  } = useContext(DashboardContext);
  const retention_days = team?.type === "outpost" ? subscription.outpost_config.retention_days : subscription.retention_days;
  let t8;
  if ($[11] !== date_range || $[12] !== timezone) {
    t8 = getTimerangeLabel(date_range, timezone);
    $[11] = date_range;
    $[12] = timezone;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const date_label = t8;
  let t10;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "baseline",
      gap: 1
    };
    t10 = {
      r: -1
    };
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  let t11;
  if ($[16] !== date_label) {
    t11 = <span>{date_label}</span>;
    $[16] = date_label;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== timezone) {
    t12 = getTimezoneAbbreviation(timezone);
    $[18] = timezone;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12) {
    t13 = <Text as="span" muted={true} semi={true} size="xs">{t12}</Text>;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t13) {
    t14 = <Div flex={t9} m={t10}>{t11}{t13}</Div>;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== browser_timezone || $[26] !== form_initial_values || $[27] !== onDateRangeChanged || $[28] !== retention_days || $[29] !== updateTimezonePreference) {
    t15 = toggle => <Formik initialValues={form_initial_values} onSubmit={async values => {
      const selected_timezone = resolveTimezoneOption(values.timezone, browser_timezone);
      await updateTimezonePreference(values.timezone === BROWSER_TIMEZONE_OPTION ? null : values.timezone);
      const {
        start_date,
        end_date
      } = formatDateFilterValues({
        start_date: values.date_range.min,
        start_time: values.start_time,
        end_date: values.date_range.max,
        end_time: values.end_time
      }, selected_timezone);
      onDateRangeChanged({
        relative: values.date_range.relative || undefined,
        min: start_date,
        max: end_date
      });
      toggle(false);
    }}>{t16 => {
        const {
          handleSubmit
        } = t16;
        return <Form><StyledCardSection><DatePickerInput name="date_range" display_reset={false} time_format="hh:mm:ss" retention_days={retention_days} /><Div flex={{
              justify: "flex-end",
              gap: 3
            }} p={{
              t: 0,
              x: 4,
              b: 4
            }}><Button outline={true} onClick={() => toggle(false)}>Cancel</Button><Button primary={true} onClick={() => handleSubmit()}>Apply</Button></Div></StyledCardSection></Form>;
      }}</Formik>;
    $[25] = browser_timezone;
    $[26] = form_initial_values;
    $[27] = onDateRangeChanged;
    $[28] = retention_days;
    $[29] = updateTimezonePreference;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== portal || $[32] !== t14 || $[33] !== t15) {
    t16 = <Dropdown outline={true} expand_icon="chevron_down" label={t14} icon="date" portal={portal}>{t15}</Dropdown>;
    $[31] = portal;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
export default MetricDatePicker;